import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/Social.css';

const API = process.env.REACT_APP_SERVER_URL || '';
const headers = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const GuildPanel = ({ character, onClose, onCharacterUpdate }) => {
  const [guild, setGuild] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState('info'); // info | members | invite | settings
  const [createName, setCreateName] = useState('');
  const [createTag, setCreateTag] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState(null);
  const searchTimer = useRef(null);
  const suggestionsRef = useRef(null);
  const inviteInputRef = useRef(null);

  const fetchGuild = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/guild/my`, { headers: headers() });
      const data = await res.json();
      if (res.ok && data.guild) {
        setGuild(data.guild);
      } else {
        setGuild(null);
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchInvites = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/guild/invites`, { headers: headers() });
      const data = await res.json();
      if (res.ok) setInvites(data.invites || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    Promise.all([fetchGuild(), fetchInvites()]).finally(() => setLoading(false));
  }, [fetchGuild, fetchInvites]);

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const doPost = async (endpoint, body = {}) => {
    try {
      const res = await fetch(`${API}/api/guild/${endpoint}`, {
        method: 'POST', headers: headers(), body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) { flash(data.error || 'Request failed', true); return null; }
      return data;
    } catch (e) { flash('Network error', true); return null; }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const data = await doPost('create', { name: createName, tag: createTag, description: createDesc });
    if (data) {
      flash('Guild created!');
      await fetchGuild();
      if (onCharacterUpdate) onCharacterUpdate();
    }
  };

  const handleAcceptInvite = async (guildId) => {
    const data = await doPost('accept-invite', { guildId });
    if (data) {
      flash('You joined the guild!');
      await fetchGuild();
      await fetchInvites();
      if (onCharacterUpdate) onCharacterUpdate();
    }
  };

  const handleInvite = async (e) => {
    if (e) e.preventDefault();
    if (!inviteName.trim()) return;
    const data = await doPost('invite', { characterName: inviteName });
    if (data) {
      flash(`Invite sent to ${inviteName}`);
      setInviteName('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const updateDropdownPos = useCallback(() => {
    if (inviteInputRef.current) {
      const rect = inviteInputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, []);

  const searchPlayers = useCallback(async (query) => {
    if (query.length < 1) { setSuggestions([]); setShowSuggestions(false); return; }
    try {
      const res = await fetch(`${API}/api/social/search-players?q=${encodeURIComponent(query)}`, { headers: headers() });
      const data = await res.json();
      if (res.ok) {
        // Filter out players already in the guild
        const memberIds = new Set((guild?.members || []).map(m => m.id));
        const filtered = (data.players || []).filter(p => !memberIds.has(p.id));
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
        setHighlightIdx(-1);
      }
    } catch (e) { /* ignore */ }
  }, [guild]);

  const handleInviteInputChange = (e) => {
    const val = e.target.value;
    setInviteName(val);
    updateDropdownPos();
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (val.trim().length >= 1) {
      searchTimer.current = setTimeout(() => searchPlayers(val.trim()), 250);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (player) => {
    setInviteName(player.name);
    setSuggestions([]);
    setShowSuggestions(false);
    // Auto-send the invite
    (async () => {
      const data = await doPost('invite', { characterName: player.name });
      if (data) {
        flash(`Invite sent to ${player.name}`);
        setInviteName('');
      }
    })();
  };

  const handleInviteKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[highlightIdx]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleKick = async (characterId, name) => {
    if (!window.confirm(`Kick ${name} from the guild?`)) return;
    const data = await doPost('kick', { characterId });
    if (data) { flash(`${name} was kicked`); await fetchGuild(); }
  };

  const handlePromote = async (characterId) => {
    const data = await doPost('promote', { characterId });
    if (data) { flash('Member promoted'); await fetchGuild(); }
  };

  const handleDemote = async (characterId) => {
    const data = await doPost('demote', { characterId });
    if (data) { flash('Member demoted'); await fetchGuild(); }
  };

  const handleTransfer = async (characterId, name) => {
    if (!window.confirm(`Transfer leadership to ${name}? This cannot be undone.`)) return;
    const data = await doPost('transfer', { characterId });
    if (data) { flash('Leadership transferred'); await fetchGuild(); }
  };

  const handleLeave = async () => {
    if (!window.confirm('Leave this guild?')) return;
    const data = await doPost('leave');
    if (data) {
      flash('You left the guild');
      setGuild(null);
      if (onCharacterUpdate) onCharacterUpdate();
    }
  };

  const handleDisband = async () => {
    if (!window.confirm('Disband this guild? This cannot be undone!')) return;
    const data = await doPost('disband');
    if (data) {
      flash('Guild disbanded');
      setGuild(null);
      if (onCharacterUpdate) onCharacterUpdate();
    }
  };

  const isLeader = guild && guild.leader_id === character?.id;
  const isOfficer = guild && guild.myRank === 'officer';
  const canManage = isLeader || isOfficer;

  const getRankIcon = (rank) => {
    switch (rank) {
      case 'leader': return '👑';
      case 'officer': return '⚔️';
      default: return '🛡️';
    }
  };

  if (loading) return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content guild-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-loading">Loading...</div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content guild-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚔️ {guild ? guild.name : 'Guild'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="guild-flash error">{error}</div>}
        {success && <div className="guild-flash success">{success}</div>}

        {/* No guild — show create form + pending invites */}
        {!guild && (
          <div className="guild-no-guild">
            {invites.length > 0 && (
              <div className="guild-invites-section">
                <h3>📜 Pending Invites</h3>
                {invites.map(inv => (
                  <div key={inv.guild_id} className="guild-invite-row">
                    <span className="guild-invite-name">[{inv.tag}] {inv.name}</span>
                    <span className="guild-invite-from">from {inv.inviter_name}</span>
                    <button className="btn-sm btn-accept" onClick={() => handleAcceptInvite(inv.guild_id)}>Accept</button>
                  </div>
                ))}
              </div>
            )}

            <div className="guild-create-section">
              <h3>🏰 Create a Guild</h3>
              {(character?.level || 0) < 10 ? (
                <p className="guild-level-req">You must be level 10 to create a guild.</p>
              ) : (
                <form onSubmit={handleCreate} className="guild-create-form">
                  <input
                    type="text" placeholder="Guild Name" maxLength={100}
                    value={createName} onChange={e => setCreateName(e.target.value)} required
                  />
                  <input
                    type="text" placeholder="Tag (2-5 chars)" maxLength={5} minLength={2}
                    value={createTag} onChange={e => setCreateTag(e.target.value)} required
                  />
                  <textarea
                    placeholder="Description (optional)" maxLength={500}
                    value={createDesc} onChange={e => setCreateDesc(e.target.value)}
                  />
                  <button type="submit" className="btn-primary">Create Guild</button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Has guild — show tabs */}
        {guild && (
          <>
            <div className="guild-tabs">
              <button className={`guild-tab ${tab === 'info' ? 'active' : ''}`} onClick={() => setTab('info')}>Info</button>
              <button className={`guild-tab ${tab === 'members' ? 'active' : ''}`} onClick={() => setTab('members')}>
                Members ({guild.members?.length || 0})
              </button>
              {canManage && (
                <button className={`guild-tab ${tab === 'invite' ? 'active' : ''}`} onClick={() => setTab('invite')}>Invite</button>
              )}
              {isLeader && (
                <button className={`guild-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>Settings</button>
              )}
            </div>

            {tab === 'info' && (
              <div className="guild-info">
                <div className="guild-tag-display">[{guild.tag}]</div>
                <p className="guild-desc">{guild.description || 'No description set.'}</p>
                <div className="guild-stats-row">
                  <span>Members: {guild.members?.length || 0}</span>
                  <span>Leader: {guild.members?.find(m => m.guild_rank === 'leader')?.name || '—'}</span>
                </div>
                <button className="btn-danger btn-sm guild-leave-btn" onClick={handleLeave}>Leave Guild</button>
              </div>
            )}

            {tab === 'members' && (
              <div className="guild-members-list">
                {(guild.members || [])
                  .sort((a, b) => {
                    const order = { leader: 0, officer: 1, member: 2 };
                    return (order[a.guild_rank] || 2) - (order[b.guild_rank] || 2);
                  })
                  .map(m => (
                    <div key={m.id} className="guild-member-row">
                      <span className="guild-member-rank">{getRankIcon(m.guild_rank)}</span>
                      <span className="guild-member-name">{m.name}</span>
                      <span className="guild-member-level">Lv.{m.level}</span>
                      <span className="guild-member-class">{m.advanced_class || m.class}</span>
                      {canManage && m.id !== character?.id && m.guild_rank !== 'leader' && (
                        <div className="guild-member-actions">
                          {isLeader && m.guild_rank === 'member' && (
                            <button className="btn-xs" onClick={() => handlePromote(m.id)} title="Promote to Officer">⬆️</button>
                          )}
                          {isLeader && m.guild_rank === 'officer' && (
                            <button className="btn-xs" onClick={() => handleDemote(m.id)} title="Demote to Member">⬇️</button>
                          )}
                          {isLeader && (
                            <button className="btn-xs" onClick={() => handleTransfer(m.id, m.name)} title="Transfer Leadership">👑</button>
                          )}
                          <button className="btn-xs btn-danger" onClick={() => handleKick(m.id, m.name)} title="Kick">✕</button>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}

            {tab === 'invite' && canManage && (
              <div className="guild-invite-section">
                <form onSubmit={handleInvite} className="guild-invite-form">
                  <div className="autocomplete-wrapper">
                    <input
                      ref={inviteInputRef}
                      type="text" placeholder="Search player name..."
                      value={inviteName}
                      onChange={handleInviteInputChange}
                      onKeyDown={handleInviteKeyDown}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      onFocus={() => { updateDropdownPos(); if (suggestions.length > 0) setShowSuggestions(true); }}
                      autoComplete="off"
                      required
                    />
                    {showSuggestions && dropdownPos && (
                      <div className="autocomplete-dropdown autocomplete-dropdown-fixed" ref={suggestionsRef}
                        style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
                        {suggestions.map((p, idx) => (
                          <div
                            key={p.id}
                            className={`autocomplete-item ${idx === highlightIdx ? 'highlighted' : ''} ${p.is_friend ? 'is-friend' : ''}`}
                            onMouseDown={() => handleSuggestionClick(p)}
                            onMouseEnter={() => setHighlightIdx(idx)}
                          >
                            <span className="autocomplete-name">
                              {p.is_friend && <span className="friend-badge" title="Friend">⭐</span>}
                              {p.name}
                            </span>
                            <span className="autocomplete-details">
                              Lv.{p.level} {p.advanced_class || p.class}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="submit" className="btn-primary">Send Invite</button>
                </form>
              </div>
            )}

            {tab === 'settings' && isLeader && (
              <div className="guild-settings">
                <button className="btn-danger" onClick={handleDisband}>⚠️ Disband Guild</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GuildPanel;
